import React, { useEffect, useMemo, useReducer } from 'react';

// Components and Others
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { modules } from 'utils/enum/modules';
import { Link as RouterLink } from 'react-router-dom';
import DateUtils from 'lib/DateUtils';
import StringUtils from 'lib/StringUtils';
import PhoneNumberUtil from 'lib/PhoneUtils';
import Skeleton from '@material-ui/lab/Skeleton';
import SearchIcon from '@material-ui/icons/Search';
import LabelValue from 'components/widgets/LabelValue';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import CustomerDialog from 'components/modules/customer/read/CustomerDialog';
import { ReactComponent as DeleteOutlineOutlinedIcon } from 'assets/remove.svg';
import { BorderColorOutlined as EditIcon, InsertCommentOutlined, BlockOutlined } from '@material-ui/icons';

// Material UI
import {
    Grid, Paper, IconButton, Typography, makeStyles, Button,
    Divider,
} from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';

// Utils
import If from 'components/widgets/conditional/If';
import EditCustomerDialog from 'components/widgets/customer/EditCustomerDialog';
import PaperAddInformation from 'components/widgets/form/PaperAddInformation';
import { isFinite } from 'lodash';
import { MEDIA_QUERY } from 'utils/enum/Core';
import CustomerService from 'services/modules/CustomerService';
import MergeDialog from 'components/modules/customer/merge/MergeDialog';
import FormattedTooltip from 'components/widgets/FormattedTooltip';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import ModalUtils from 'utils/ModalUtils';

const useStyles = makeStyles((theme) => ({
    paper: {
        minHeight: 250,
        maxHeight: 250,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 1px 4px rgba(0,0,0, 0.1)',
        width: '100%',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            minHeight: '320px',
            maxHeight: '320px',
        },
    },
    main: {
        width: '100%',
    },
    updateIcon: {
        color: theme.palette.text.purpure,
    },
    updateButton: {
        marginRight: theme.spacing(0.5),
        color: theme.palette.text.boulder,
        '&:disabled svg': {
            color: `rgba(${theme.palette.rgb.black}, 0.26)`,
        },
    },
    deleteButton: {
        marginRight: theme.spacing(0.5),
    },
    editIcon: {
        height: '18px',
        width: '18px',
    },
    editButton: {
        color: theme.palette.text.purpure,
    },
    boxHeader: {
        padding: theme.spacing(1, 2),
        minHeight: 38,
    },
    cellPhone: {
        display: 'flex',
        alignItems: 'center',
    },
    iconContainer: {
        marginLeft: theme.spacing(1),
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    message: {
        color: '#ed9a11',
    },
    block: {
        position: 'absolute',
        color: 'red',
    },
    lineSkeleton: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0 26px',
    },
    customerGrid: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            flexDirection: 'column',
            '& .MuiGrid-grid-xs-6': {
                maxWidth: '100%',
                flexBasis: '100% !important',
                marginBottom: theme.spacing(0),
            },
        },
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        '& $title': {
            margin: 0,
            lineHeight: 1.2,
        },
    },
    title: {
        margin: 0,
        lineHeight: 1.2,
    },
    customerCodeWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    customerCodeText: {
        margin: 0,
        lineHeight: 1.2,
    },
    divider: {
        display: 'inline-block',
        height: '20px',
        margin: '0 5px',
        verticalAlign: 'middle',
    },
    tooltipBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    tooltipButton: {
        textTransform: 'none',
        alignSelf: 'flex-start',
        minHeight: 0,
        padding: theme.spacing(0.5, 1),
    },
    warningIconButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        padding: 0,
        marginTop: 1,
    },
    warningIcon: {
        color: theme.palette.error.main,
        fontSize: '1.1rem',
        cursor: 'pointer',
        display: 'block',
    },
}));

const BasicCustomerInformation = ({
    record, title, open, onClickPrimaryDialog, showDeleteOption, loading,
    showEditCustomer, hasWritePermission, onClickPrimary, onClickRemove,
    disablePrimaryButtonDialog, onClickSecondaryDialog, displayLink,
    isCustomerModule, onUseSuggested, duplicates: propDuplicates, onMergeCompleted: propOnMergeCompleted,
}) => {
    const classes = useStyles();
    const hasCostumer = isFinite(record.customerCode);

    const initialState = {
        openDelete: false,
        openCustomerDialog: false,
        openMergeDialog: false,
        localDuplicates: [],
    };
    const [state, setState] = useReducer((old, action) => ({
        ...old,
        ...action,
    }), initialState);
    const {
        openDelete,
        openCustomerDialog,
        openMergeDialog,
        localDuplicates,
    } = state;

    const fullName = `${record.firstName || ''} ${record.lastName || ''}`;
    const cellPhone = PhoneNumberUtil.formatPhone(record.cellPhone);
    const { allowTextCellPhone } = record;

    const duplicates = propDuplicates || localDuplicates;

    const canMerge = KeyStore.isAllowed(Permission.CUSTOMER_MERGE);
    const customerService = useMemo(() => new CustomerService(), []);

    useEffect(() => {
        let active = true;
        if (!hasCostumer || !canMerge || !isCustomerModule || propDuplicates != null) return undefined;

        customerService.getCustomerDuplicatesForMerge(record.customerCode)
            .then((response) => {
                if (!active) return;
                const { data, graphQLErrors } = response;
                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }
                setState({ localDuplicates: data?.getCustomerDuplicatesForMerge || [] });
            })
            .catch((error) => {
                if (!active) return;
                ModalUtils.errorMessage(error?.graphQLErrors || [error]);
            });

        return () => {
            active = false;
        };
    }, [hasCostumer, canMerge, isCustomerModule, customerService, record.customerCode, propDuplicates]);

    const handleMergeCompleted = () => {
        setState({ openMergeDialog: false, localDuplicates: [] });
        if (propOnMergeCompleted) {
            propOnMergeCompleted();
            return;
        }
        if (!hasCostumer || !canMerge || !isCustomerModule || propDuplicates != null) return;

        customerService.getCustomerDuplicatesForMerge(record.customerCode)
            .then((response) => {
                const { data, graphQLErrors } = response;
                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }
                setState({ localDuplicates: data?.getCustomerDuplicatesForMerge || [] });
            })
            .catch((error) => {
                ModalUtils.errorMessage(error?.graphQLErrors || [error]);
            });
    };

    return (
        <div className={classes.main}>
            {loading && (
                <Paper
                    elevation={0}
                    square
                    className={clsx(classes.paper)}
                >
                    <div className={clsx(classes.boxHeader, 'd-flex-center-space-between')}>
                        <Skeleton width="18%" height={20} />
                    </div>
                    <Grid container spacing={0}>
                        <Grid
                            item
                            className={classes.customer}
                            xs={6}
                        >
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                        </Grid>
                        <Grid
                            item
                            className={classes.customer}
                            xs={6}
                        >
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                            <div className={classes.lineSkeleton}>
                                <Skeleton width="45%" height={20} />
                                <Skeleton width="45%" height={20} />
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <If condition={hasCostumer}>
                <Paper
                    elevation={0}
                    square
                    className={clsx(classes.paper)}
                >
                    <div className={clsx(classes.boxHeader, 'd-flex-center-space-between')}>
                        <div className={classes.titleContainer}>
                            <Typography variant="h5" color="primary" className={classes.title}>{title}</Typography>
                            <div className={classes.customerCodeWrap}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    className={clsx(classes.title, classes.customerCodeText)}
                                    component="span"
                                >
                                    {displayLink ? (
                                        <RouterLink
                                            to={{ pathname: `/${modules.CUSTOMERS}/${record.customerCode}` }}
                                            className="link"
                                            target="_blank"
                                        >
                                            {`#${record.customerCode}`}
                                        </RouterLink>
                                    ) : `#${record.customerCode}`}
                                </Typography>
                                {canMerge && duplicates.length > 0 && (
                                    <FormattedTooltip
                                        placement="top"
                                        interactive
                                        header="Possible Duplicates Found:"
                                        body={(
                                            <div className={classes.tooltipBody}>
                                                <span>
                                                    {`We found ${duplicates.length} potential duplicate `}
                                                    {`record${duplicates.length === 1 ? '' : 's'} for this customer.`}
                                                </span>
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    size="small"
                                                    className={classes.tooltipButton}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setState({ openMergeDialog: true });
                                                    }}
                                                >
                                                    Review and Merge
                                                </Button>
                                            </div>
                                        )}
                                    >
                                        <span className={classes.warningIconButton}>
                                            <WarningOutlinedIcon className={classes.warningIcon} />
                                        </span>
                                    </FormattedTooltip>
                                )}
                            </div>
                        </div>
                        <div>
                            <If condition={hasWritePermission && showDeleteOption}>
                                <IconButton
                                    size="small"
                                    aria-label="Remove"
                                    className={classes.deleteButton}
                                    onClick={() => setState({ openDelete: true })}
                                >
                                    <DeleteOutlineOutlinedIcon />
                                </IconButton>
                            </If>
                            {hasWritePermission && showDeleteOption && <Divider className={classes.divider} orientation="vertical" variant="middle" />}
                            <If condition={hasWritePermission}>
                                <IconButton
                                    size="small"
                                    className={classes.updateButton}
                                    onClick={onClickPrimary}
                                >
                                    <SearchIcon className={classes.updateIcon} />
                                </IconButton>
                            </If>
                            <If condition={showEditCustomer}>
                                <Button
                                    color="primary"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => setState({ openCustomerDialog: true })}
                                >
                                    Edit
                                </Button>
                            </If>
                        </div>
                    </div>
                    <Grid container spacing={0} className={classes.customerGrid}>
                        <Grid
                            item
                            className={classes.customer}
                            xs={6}
                        >
                            <LabelValue
                                label="Name"
                                value={fullName}
                            />
                            <LabelValue
                                label="Email"
                                value={record.email}
                            />
                            <LabelValue
                                label="Cell Phone"
                                value={(
                                    <div className={classes.cellPhone}>
                                        {cellPhone}
                                        <If condition={!StringUtils.isEmpty(cellPhone)}>
                                            <div className={classes.iconContainer}>
                                                {!allowTextCellPhone && <BlockOutlined className={classes.block} />}
                                                <InsertCommentOutlined fontSize="small" className={classes.message} />
                                            </div>
                                        </If>
                                    </div>
                                )}
                            />
                            <LabelValue
                                label="Home Phone"
                                value={PhoneNumberUtil.formatPhone(record.homePhone)}
                            />
                            <LabelValue
                                label="Work Phone"
                                value={PhoneNumberUtil.formatPhone(record.workPhone)}
                            />
                        </Grid>
                        <Grid
                            item
                            className={classes.customer}
                            xs={6}
                        >
                            <LabelValue
                                label="DOB"
                                value={DateUtils.getOnlyDate(record.dob)}
                            />
                            <LabelValue
                                label="SSN"
                                value={StringUtils.applySSNMask(record.ssn)}
                            />
                            <LabelValue
                                label="DLN"
                                value={record.dln}
                            />
                            <LabelValue
                                label="Language"
                                value={record.language}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </If>
            <If condition={open}>
                <CustomerDialog
                    open={open}
                    toggleModal={onClickSecondaryDialog}
                    title={title}
                    onSelectRow={onClickPrimaryDialog}
                    disablePrimaryButton={disablePrimaryButtonDialog}
                />
            </If>
            <If condition={openCustomerDialog}>
                <EditCustomerDialog
                    open={openCustomerDialog}
                    record={record}
                    toggleModal={() => setState({ openCustomerDialog: false })}
                    isCustomerModule={isCustomerModule}
                    onUseSuggested={onUseSuggested}
                    onMergeCompleted={handleMergeCompleted}
                />
            </If>
            <If condition={!loading && !hasCostumer && hasWritePermission}>
                <PaperAddInformation
                    label={`Add ${title}`}
                    onClickLeftLabel={onClickPrimary}
                />
            </If>
            <If condition={openDelete}>
                <ConfirmDialog
                    title={`Confirm remove ${title}`}
                    description={`Are you sure you want to remove the ${title}?`}
                    open={openDelete}
                    titlePrimary="Yes"
                    titleSecondary="No"
                    variant="outlined"
                    dividerFooter={false}
                    onClickPrimary={() => {
                        setState({ openDelete: false });
                        onClickRemove();
                    }}
                    onClose={() => setState({ openDelete: false })}
                    onClickSecondary={() => setState({ openDelete: false })}
                />
            </If>

            {openMergeDialog && (
                <MergeDialog
                    open={openMergeDialog}
                    masterCustomerCode={Number(record.customerCode)}
                    secondaryCustomerCodes={duplicates.map((item) => Number(item.customerCode))}
                    suggestedCustomer={duplicates}
                    showReviewStep
                    onClose={() => setState({ openMergeDialog: false })}
                    onMergeCompleted={handleMergeCompleted}
                />
            )}
        </div>
    );
};

BasicCustomerInformation.propTypes = {
    loading: PropTypes.bool,
    open: PropTypes.bool,
    title: PropTypes.string,
    record: PropTypes.object,
    displayLink: PropTypes.bool,
    onClickRemove: PropTypes.func,
    onClickPrimary: PropTypes.func,
    showEditCustomer: PropTypes.bool,
    showDeleteOption: PropTypes.bool,
    hasWritePermission: PropTypes.bool,
    onClickPrimaryDialog: PropTypes.func,
    onClickSecondaryDialog: PropTypes.func,
    disablePrimaryButtonDialog: PropTypes.bool,
    isCustomerModule: PropTypes.bool,
    onUseSuggested: PropTypes.func,
    duplicates: PropTypes.array,
    onMergeCompleted: PropTypes.func,
};

BasicCustomerInformation.defaultProps = {
    record: {},
    open: false,
    title: 'Buyer',
    loading: false,
    displayLink: true,
    showDeleteOption: false,
    showEditCustomer: false,
    onClickRemove: () => {},
    onClickPrimary: () => {},
    hasWritePermission: false,
    onClickPrimaryDialog: () => {},
    onClickSecondaryDialog: () => {},
    disablePrimaryButtonDialog: false,
    isCustomerModule: false,
    onUseSuggested: null,
    duplicates: null,
    onMergeCompleted: null,
};

export default BasicCustomerInformation;
